'use client'

import { ReactNode, useEffect } from 'react'
import { Cell, Icon, Text } from '@vinted/web-ui'
import { CheckmarkShield16 } from '@vinted/monochrome-icons'

import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/shared/ab-tests'

import { FaqEntryType } from 'constants/faq-entry'
import FaqEntryUrl from 'components/FaqEntryUrl'
import { clickEvent } from '_libs/common/event-tracker/events'
import { ClickableElement } from 'constants/tracking/clickable-elements'

type Props = {
  threadId: string | number | undefined
}

const PhishingReminderBanner = ({ threadId }: Props) => {
  const { track } = useTracking()

  const trackAbTest = useTrackAbTestCallback()
  const phishingReminderBannerV2AbTest = useAbTest('phishing_reminder_banner_v2')

  const translate = useTranslate('conversation.phishing_passive_reminder.banner')

  useEffect(() => {
    if (!phishingReminderBannerV2AbTest) return

    trackAbTest(phishingReminderBannerV2AbTest)
  }, [trackAbTest, phishingReminderBannerV2AbTest])

  const handleLinkClick = () => {
    track(
      clickEvent({
        target: ClickableElement.PhishingReminderDialogLearnMore,
        targetDetails: JSON.stringify({
          thread_id: threadId,
        }),
      }),
    )
  }

  const renderBody = () => (
    <Text
      as="p"
      type={Text.Type.Caption}
      text={translate('body', {
        'more-safety-tips-link': (chunks: Array<ReactNode>) => (
          <FaqEntryUrl
            key="more-safety-tips-link"
            type={FaqEntryType.SpamPhishing}
            render={faqUrl => (
              <a href={faqUrl} target="_blank" rel="noreferrer" onClick={handleLinkClick}>
                {chunks}
              </a>
            )}
          />
        ),
      })}
    />
  )

  if (phishingReminderBannerV2AbTest?.variant !== 'on') return null

  return (
    <Cell
      prefix={
        <Icon
          color={Icon.Color.Primary}
          name={CheckmarkShield16}
          aria={{
            'aria-hidden': 'true',
          }}
        />
      }
      body={renderBody()}
      highlighted
    />
  )
}

export default PhishingReminderBanner
